import { type ProductEntry, ProductJourney } from "@shared/analytics/productEvents";
import {
    convertAssetToSelectOption,
    convertRawToToken,
    convertStringtoRaw,
    formatNumber,
    formatNumberSignificantDigits as formatNumberSignificantDigitsBase,
    type SelectOption,
    type Token,
    USDV_MINT,
    useSessionStore,
    useTokenBalancesActions,
    useVaultStore,
    useWallet,
    withWalletBalanceMutationLock
} from "@solomon-apps/shared";
import { formatBalance, useAutoDismissUserCancel } from "@solomon-apps/shared";
import type { SwapMode, SwapSnapshot, SwapView, UseSwapReturn as UseSwapReturnBase } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { WELL_KNOWN_TOKENS_STRINGS } from "@solomon-labs/constants";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { toast } from "sonner";

import { swapAmountBucket, swapControlCapture, swapToken } from "@/services/analytics/swap";

import { getExactHoldingAmount, getNativeSolUiAmount } from "./balanceAmounts";
import { getQuotePriceImpact, getSwapUsdValue } from "./quotePricing";
import { resolveSwapToken } from "./swapTokens";
import {
    buildJupiterExecuteParams,
    getQuoteRejection,
    type JupiterUltraQuoteResponse,
    useJupiterExecute,
    useJupiterHoldings,
    useJupiterQuote,
    useJupiterTokenSearchBatch
} from "./useJupiter";
import { useSwapFeedback } from "./useSwapFeedback";

/**
 * Threshold-dispatched balance formatter. Rewrites the shared
 * `formatNumberSignificantDigits` so it can NEVER emit scientific notation:
 *   - For values ≥ 1e6 → delegate to `formatBalance` (1,234,567 / 12.5M / etc.)
 *   - Below that, keep the small-number significant-digits formatting that the
 *     shared util has today (used for tiny token balances < 1).
 *
 * The shared util uses `.toString()` for values < 1, which CAN emit scientific
 * notation for extremely small magnitudes; we route ≥1e6 through formatBalance
 * to also handle the upper end gracefully without compact-notation surprises.
 */
function formatNumberSignificantDigits(amount: number): string {
    if (!Number.isFinite(amount)) return "—";
    if (Math.abs(amount) >= 1_000_000) return formatBalance(amount);
    return formatNumberSignificantDigitsBase(amount);
}

// ─── Types ───────────────────────────────────────────────────────────────────

// SwapSnapshot / SwapMode / SwapView / UseSwapReturn hoisted to
// `@solomon-apps/shared` as part of the Phase 1D refactor so the
// widgets (which live in shared) can type-narrow the orchestrator's
// generic without referencing `@/`. apps/main re-exports them here
// for back-compat with existing consumers (apps/main pages).
export type { SwapSnapshot, SwapMode, SwapView, UseSwapReturn as UseSwapReturnBase } from "@solomon-apps/shared";

type FeeFooterDisplay = {
    priceImpactDisplay: string;
    routingFeeDisplay: string;
    networkFeeDisplay: string;
};

/**
 * Production `UseSwapReturn` — same shape as the shared structural
 * `UseSwapReturnBase`, with `quote` typed to the precise
 * `JupiterUltraQuoteResponse` so apps/main-side consumers get full
 * type inference. Shared widgets don't consume the SDK quote.
 */
export interface UseSwapReturn extends UseSwapReturnBase {
    quote: JupiterUltraQuoteResponse | undefined;
}

const LAMPORTS_PER_SOL = 1_000_000_000;
const USD_FORMAT_OPTIONS = { minimumFractionDigits: 2, maximumFractionDigits: 2 } as const;
const BUY_SEND_SYMBOL_ORDER = ["SOL", "USDC", "USDG", "USDT", "PYUSD"] as const;
const BUY_SEND_SYMBOL_RANK = new Map<string, number>(BUY_SEND_SYMBOL_ORDER.map((symbol, index) => [symbol, index]));

function sortBuySendTokens(tokens: Token[]): Token[] {
    return tokens
        .map((token, index) => ({ token, index }))
        .sort((a, b) => {
            const aRank = BUY_SEND_SYMBOL_RANK.get(a.token.symbol) ?? Number.MAX_SAFE_INTEGER;
            const bRank = BUY_SEND_SYMBOL_RANK.get(b.token.symbol) ?? Number.MAX_SAFE_INTEGER;

            return aRank === bRank ? a.index - b.index : aRank - bRank;
        })
        .map(({ token }) => token);
}

function isCurrentUsdv(token: Token): boolean {
    return token.address === USDV_MINT;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useSwap(options: { entry_point?: ProductEntry; analytics?: ProductJourney } = {}): UseSwapReturn {
    const [mode, setMode] = React.useState<SwapMode>("buy");

    // Stores & hooks
    const tokens = useSessionStore((s) => s.tokens);
    const rates = useVaultStore((s) => s.rates);
    const { connected, publicKey } = useWallet();
    const analyticsAddress = publicKey?.toBase58();
    const analyticsEntry = options.entry_point ?? "swap_widget";
    const fallbackAnalytics = React.useMemo(
        () => new ProductJourney("swap", analyticsEntry, analyticsAddress),
        [analyticsEntry, analyticsAddress]
    );
    const analytics = options.analytics ?? fallbackAnalytics;
    const capture = React.useMemo(() => swapControlCapture(analytics), [analytics]);
    const { reconcileWithOptimisticDelta } = useTokenBalancesActions();

    // Send / receive amounts & currencies
    const [sendAmount, setSendAmount] = React.useState<string>("");
    const [receiveAmount, setReceiveAmount] = React.useState<string>("");
    const [sendCurrency, setSendCurrency] = React.useState<string>("");
    const [receiveCurrency, setReceiveCurrency] = React.useState<string>("");

    // Asset option lists
    const [sendAssetOptions, setSendAssetOptions] = React.useState<SelectOption[]>([]);
    const [receiveAssetOptions, setReceiveAssetOptions] = React.useState<SelectOption[]>([]);
    const [sendSelectedOption, setSendSelectedOption] = React.useState<SelectOption | null>(null);
    const [receiveSelectedOption, setReceiveSelectedOption] = React.useState<SelectOption | null>(null);

    // View state: which screen to show
    const [swapView, setSwapView] = React.useState<SwapView>("form");

    // Snapshot swap details when execution starts (amounts get cleared on success)
    const swapSnapshotRef = React.useRef<SwapSnapshot | null>(null);

    // Track initialization
    const hasInitializedRef = React.useRef(false);

    // ─── Analytics refs ──────────────────────────────────────────────────────
    // Once-per-session-per-(mode×field) guard for `swap_amount_first_entered`.
    const amountFirstEnteredKeysRef = React.useRef<Set<string>>(new Set());
    // Captures when quoteParams become truthy → drives `quote_latency_ms`.
    const quoteRequestedAtRef = React.useRef<number | null>(null);
    // Last quote signature so we only fire `swap_quote_received` once per fetch.
    const lastQuoteSignatureRef = React.useRef<string | null>(null);
    // Last quote-error key so we only fire `swap_quote_failed` once per error.
    const lastQuoteErrorKeyRef = React.useRef<string | null>(null);
    // Card min-height for pending/success screens
    const [cardMinHeightValue, setCardMinHeightValue] = React.useState<number | undefined>(undefined);

    // Jupiter Holdings as single source of truth for balances
    const queryClient = useQueryClient();
    const walletAddress = publicKey?.toBase58() || null;
    const { data: holdings } = useJupiterHoldings(walletAddress);

    /** True once we actually know what the wallet holds. While this is false
     *  every balance is UNKNOWN — never zero — and must not be rendered as a
     *  number (see `formatDisplayBalance`). */
    const areBalancesKnown = connected && holdings !== undefined;

    const getBalanceFromHoldings = (tokenAddress: string): number | null => {
        if (!connected || !holdings) return null;
        if (tokenAddress === WELL_KNOWN_TOKENS_STRINGS.SOL) {
            // Native SOL lives at the top level of the Ultra payload; `native`
            // is only a mirror of it and may be absent.
            return getNativeSolUiAmount(holdings);
        }
        const accounts = holdings.tokens?.[tokenAddress];
        if (!accounts || accounts.length === 0) return null;
        return accounts.reduce((sum, account) => sum + (account.uiAmount ?? 0), 0);
    };

    // ─── Token metadata ──────────────────────────────────────────────────────
    // Jupiter token metadata for both legs: decimals/symbol/logo for tokens the
    // session list doesn't know (the picker offers any routable token), plus
    // the spot USD prices used below.

    const tokenMetadataMints = React.useMemo(() => {
        const mints: string[] = [];
        if (sendCurrency) mints.push(sendCurrency);
        if (receiveCurrency && receiveCurrency !== sendCurrency) {
            mints.push(receiveCurrency);
        }
        return mints;
    }, [sendCurrency, receiveCurrency]);
    const { data: tokenMetadata } = useJupiterTokenSearchBatch(tokenMetadataMints);
    const sendToken = React.useMemo(
        () => resolveSwapToken(sendCurrency, tokens, tokenMetadata),
        [sendCurrency, tokens, tokenMetadata]
    );
    const receiveToken = React.useMemo(
        () => resolveSwapToken(receiveCurrency, tokens, tokenMetadata),
        [receiveCurrency, tokens, tokenMetadata]
    );

    // ─── Jupiter Quote ───────────────────────────────────────────────────────

    const quoteParams = React.useMemo(() => {
        if (!sendCurrency || !receiveCurrency || !sendAmount || parseFloat(sendAmount) <= 0) {
            return null;
        }

        if (!sendToken) return null;

        const rawAmount = convertStringtoRaw(sendAmount, sendToken.decimals);

        return {
            inputMint: sendCurrency,
            outputMint: receiveCurrency,
            amount: (rawAmount as BigDecimal).toString(),
            taker: walletAddress || undefined
        };
    }, [sendCurrency, receiveCurrency, sendAmount, sendToken, walletAddress]);

    const {
        data: quote,
        isLoading: isQuoteLoading,
        refetch: refetchQuote,
        isFetching: isQuoteFetching,
        error: quoteErrorRaw
    } = useJupiterQuote(quoteParams);

    // React Query types `error` as `Error | null` already, but keep the
    // assertion narrow so consumers don't need to widen.
    const quoteError: Error | null = quoteErrorRaw ?? null;
    const quoteForCurrentPair = React.useMemo(
        () => (quote?.inputMint === sendCurrency && quote.outputMint === receiveCurrency ? quote : undefined),
        [quote, sendCurrency, receiveCurrency]
    );
    const quoteRejection = React.useMemo(
        () => (quoteForCurrentPair ? getQuoteRejection(quoteForCurrentPair) : null),
        [quoteForCurrentPair]
    );
    const executeParams = React.useMemo(
        () => buildJupiterExecuteParams(quote, quoteParams, walletAddress),
        [quote, quoteParams, walletAddress]
    );
    const canExecuteQuote = executeParams !== null;

    // ─── Jupiter Execute ─────────────────────────────────────────────────────

    const { state: executeState, executeWithReceipt: executeSwap, reset: resetExecute } = useJupiterExecute();

    // ─── Effects ─────────────────────────────────────────────────────────────

    // Update receiveAmount when quote changes
    React.useEffect(() => {
        if (quoteForCurrentPair && receiveToken) {
            const outAmountRaw = new BigDecimal(quoteForCurrentPair.outAmount);
            const outAmountNative = convertRawToToken(outAmountRaw, receiveToken.decimals) as string;
            setReceiveAmount(parseFloat(outAmountNative).toFixed(4));
        } else if (!sendAmount || parseFloat(sendAmount) <= 0) {
            setReceiveAmount("");
        }
    }, [quoteForCurrentPair, receiveToken, sendAmount]);

    // Reset execute state when inputs change (clears stale success/error) -
    // form view only. `swapView` is read via a ref rather than being a dep,
    // because we want this effect to fire on input changes only, not on view
    // transitions. The previous implementation included `swapView` in deps,
    // which caused this effect to race with the "return to form on error"
    // effect below: a pending tx failing → isError=true → swapView flipped to
    // "form" → this effect re-fired because swapView changed and immediately
    // wiped isError, killing the "Buy Failed → Return to Buy" recovery UI
    // before useErrorReturnReady could open.
    const swapViewRef = React.useRef(swapView);
    React.useEffect(() => {
        swapViewRef.current = swapView;
    }, [swapView]);
    React.useEffect(() => {
        if (swapViewRef.current === "form" && (executeState.isSuccess || executeState.isError)) {
            resetExecute();
        }
    }, [sendAmount, sendCurrency, receiveCurrency, resetExecute, executeState.isSuccess, executeState.isError]);

    // Return to form on error during pending screen. The error state itself
    // (executeState.isError) persists past the view flip — phase derivation
    // resolves to "error" regardless of view, and the user dismisses via
    // handlePendingCancel which already runs resetExecute.
    React.useEffect(() => {
        if (executeState.isError && swapView === "pending") {
            setSwapView("form");
        }
    }, [executeState.isError, swapView]);

    // Asset options initialization effect
    React.useEffect(() => {
        if (!tokens.length) return;

        const isInitializing = !hasInitializedRef.current;

        if (rates && Object.keys(rates).length > 0) {
            if (mode === "buy") {
                const buySendTokens = Object.entries(rates)
                    .map(([assetAddress]) => tokens.find((t) => t.address === assetAddress))
                    .filter((token): token is Token => !!token);
                const options = sortBuySendTokens(buySendTokens).map(
                    (token) => convertAssetToSelectOption(token) as SelectOption
                );
                setSendAssetOptions(options);
                const receiveAsset = tokens.find(isCurrentUsdv);
                if (receiveAsset) {
                    setReceiveAssetOptions([convertAssetToSelectOption(receiveAsset) as SelectOption]);
                    setReceiveCurrency(receiveAsset.address);
                }
                if (isInitializing && options.length > 0) {
                    setSendCurrency(options[0].value);
                }
            } else {
                const sendAsset = tokens.find(isCurrentUsdv);
                if (sendAsset) {
                    setSendAssetOptions([convertAssetToSelectOption(sendAsset) as SelectOption]);
                    setSendCurrency(sendAsset.address);
                    setSendSelectedOption(null);
                }
                const options = Object.entries(rates)
                    .map(([assetAddress]) => tokens.find((t) => t.address === assetAddress))
                    .filter((token): token is Token => !!token)
                    .map((token) => convertAssetToSelectOption(token) as SelectOption);
                setReceiveAssetOptions(options);
                if (isInitializing && options.length > 0) {
                    setReceiveCurrency(options[0].value);
                }
            }
            hasInitializedRef.current = true;
        } else {
            if (mode === "buy") {
                // Wallet not connected: preview a short list of well-known tokens so the
                // selector isn't a single row. Real available tokens come from `rates`
                // once a wallet is connected.
                const previewSendAssets = BUY_SEND_SYMBOL_ORDER.map((symbol) =>
                    tokens.find((token) => token.symbol === symbol)
                ).filter((token): token is Token => !!token);
                const sendAsset = previewSendAssets.find((token) => token.symbol === "USDC");
                const receiveAsset = tokens.find(isCurrentUsdv);
                if (previewSendAssets.length > 0) {
                    setSendAssetOptions(
                        previewSendAssets.map((token) => convertAssetToSelectOption(token) as SelectOption)
                    );
                    if (isInitializing && sendAsset) {
                        setSendCurrency(sendAsset.address);
                    }
                }
                if (receiveAsset) {
                    setReceiveAssetOptions([convertAssetToSelectOption(receiveAsset) as SelectOption]);
                    setReceiveCurrency(receiveAsset.address);
                }
            } else {
                const sendAsset = tokens.find(isCurrentUsdv);
                const receiveAsset = tokens.find((token) => token.symbol === "USDC");
                if (sendAsset) {
                    setSendAssetOptions([convertAssetToSelectOption(sendAsset) as SelectOption]);
                    setSendCurrency(sendAsset.address);
                }
                if (receiveAsset) {
                    setReceiveAssetOptions([convertAssetToSelectOption(receiveAsset) as SelectOption]);
                    if (isInitializing) {
                        setReceiveCurrency(receiveAsset.address);
                    }
                }
            }
            hasInitializedRef.current = true;
        }
    }, [rates, tokens, mode]);

    // ─── Derived values ──────────────────────────────────────────────────────

    const sendCurrencySymbol = sendAssetOptions.find((opt) => opt.value === sendCurrency)?.label ?? sendToken?.symbol;
    const receiveCurrencySymbol =
        receiveAssetOptions.find((opt) => opt.value === receiveCurrency)?.label ?? receiveToken?.symbol;

    const sendTokenDecimals = sendToken?.decimals;
    const receiveTokenDecimals = receiveToken?.decimals;

    const sendDisplayOption =
        sendSelectedOption ??
        sendAssetOptions.find((opt) => opt.value === sendCurrency) ??
        (sendToken ? (convertAssetToSelectOption(sendToken) as SelectOption) : undefined);

    const receiveDisplayOption =
        receiveSelectedOption ??
        receiveAssetOptions.find((opt) => opt.value === receiveCurrency) ??
        (receiveToken ? (convertAssetToSelectOption(receiveToken) as SelectOption) : undefined);

    const receiveAmountDisplay = React.useMemo(() => {
        const num = parseFloat(receiveAmount);
        if (!receiveAmount || !Number.isFinite(num) || num === 0) return "";
        return formatNumber(num, { maximumFractionDigits: 4 });
    }, [receiveAmount]);

    const sendExactBalanceAmount = connected ? getExactHoldingAmount(sendCurrency, holdings, sendTokenDecimals) : null;
    const receiveExactBalanceAmount = connected
        ? getExactHoldingAmount(receiveCurrency, holdings, receiveTokenDecimals)
        : null;
    const maxBalanceAmount = sendExactBalanceAmount ?? "0";
    const maxBalanceValue = Number(maxBalanceAmount) || (getBalanceFromHoldings(sendCurrency) ?? 0);

    /**
     * Balance text for one leg. Returns `""` while the wallet's holdings are
     * unknown (disconnected, still loading, or the holdings request failed) so
     * the widget hides the row instead of asserting a balance of zero — a
     * wallet that holds 0.073 SOL must never read "Balance: 0".
     *
     * Once holdings ARE known, a token with no accounts in the payload really
     * is a zero balance and still renders as "0".
     */
    const formatDisplayBalance = (exactAmount: string | null, tokenAddress: string): string => {
        if (!areBalancesKnown) return "";
        if (exactAmount !== null) return formatNumberSignificantDigits(Number(exactAmount));
        return formatNumberSignificantDigits(getBalanceFromHoldings(tokenAddress) ?? 0);
    };

    const sendDisplayBalance = formatDisplayBalance(sendExactBalanceAmount, sendCurrency);
    const receiveDisplayBalance = formatDisplayBalance(receiveExactBalanceAmount, receiveCurrency);

    // USD value of each leg, computed from spot token prices in the Jupiter
    // token metadata. Non-stablecoins like SOL show their real USD value
    // (e.g. 2 SOL ≈ $168.74) instead of mirroring the raw token amount.
    // Prefer the Jupiter quote's `inUsdValue`/`outUsdValue` when present
    // (closer to actual swap value with routing/slippage), else fall back to
    // amount × spot price.
    const sendUsdPrice = React.useMemo(() => {
        if (!sendCurrency) return null;
        const meta = tokenMetadata.find((m) => m.id === sendCurrency);
        return typeof meta?.usdPrice === "number" && Number.isFinite(meta.usdPrice) ? meta.usdPrice : null;
    }, [tokenMetadata, sendCurrency]);
    const receiveUsdPrice = React.useMemo(() => {
        if (!receiveCurrency) return null;
        const meta = tokenMetadata.find((m) => m.id === receiveCurrency);
        return typeof meta?.usdPrice === "number" && Number.isFinite(meta.usdPrice) ? meta.usdPrice : null;
    }, [tokenMetadata, receiveCurrency]);

    const usdValue = React.useMemo(() => {
        return formatNumber(
            getSwapUsdValue({
                mint: sendCurrency,
                amount: sendAmount,
                quotedUsdValue: quoteForCurrentPair?.inUsdValue,
                quotedRawAmount: quoteForCurrentPair?.inAmount,
                spotUsdPrice: sendUsdPrice
            }),
            USD_FORMAT_OPTIONS
        );
    }, [quoteForCurrentPair, sendAmount, sendUsdPrice, sendCurrency]);

    const receiveUsdValue = React.useMemo(() => {
        return formatNumber(
            getSwapUsdValue({
                mint: receiveCurrency,
                amount: receiveAmount,
                quotedUsdValue: quoteForCurrentPair?.outUsdValue,
                quotedRawAmount: quoteForCurrentPair?.outAmount,
                spotUsdPrice: receiveUsdPrice
            }),
            USD_FORMAT_OPTIONS
        );
    }, [quoteForCurrentPair, receiveAmount, receiveUsdPrice, receiveCurrency]);

    const exchangeRatePairKey = sendCurrency && receiveCurrency ? `${sendCurrency}->${receiveCurrency}` : null;
    const [exchangeRateByPair, setExchangeRateByPair] = React.useState<Record<string, string>>({});
    const liveExchangeRate = React.useMemo(() => {
        if (!sendToken || !receiveToken) return null;

        if (!quoteForCurrentPair) return null;

        const inAmount = parseFloat(quoteForCurrentPair.inAmount);
        const outAmount = parseFloat(quoteForCurrentPair.outAmount);
        if (inAmount === 0) return null;

        const inHuman = inAmount / Math.pow(10, sendToken.decimals);
        const outHuman = outAmount / Math.pow(10, receiveToken.decimals);
        return formatNumber(outHuman / inHuman, { maximumFractionDigits: 4 });
    }, [quoteForCurrentPair, sendToken, receiveToken]);

    React.useEffect(() => {
        if (!exchangeRatePairKey || liveExchangeRate === null) return;
        setExchangeRateByPair((current) =>
            current[exchangeRatePairKey] === liveExchangeRate
                ? current
                : { ...current, [exchangeRatePairKey]: liveExchangeRate }
        );
    }, [exchangeRatePairKey, liveExchangeRate]);

    const exchangeRate =
        liveExchangeRate ?? (exchangeRatePairKey ? (exchangeRateByPair[exchangeRatePairKey] ?? null) : null);

    const quotePriceImpact = getQuotePriceImpact(quoteForCurrentPair);
    const liveFeeFooterDisplay = React.useMemo<FeeFooterDisplay | null>(() => {
        if (!quoteForCurrentPair) return null;

        const feeBps = quoteForCurrentPair.feeBps ?? 0;
        const routingFeeDisplay = feeBps <= 0 ? "FREE" : `${formatNumber(feeBps / 100, { maximumFractionDigits: 2 })}%`;

        let networkFeeDisplay = "FREE";
        if (quoteForCurrentPair.gasless && feeBps > (quoteForCurrentPair.platformFee?.feeBps ?? 0)) {
            networkFeeDisplay = "Included in fee";
        } else if (!quoteForCurrentPair.gasless) {
            const totalLamports =
                (quoteForCurrentPair.signatureFeeLamports ?? 0) +
                (quoteForCurrentPair.prioritizationFeeLamports ?? 0) +
                (quoteForCurrentPair.rentFeeLamports ?? 0);

            if (totalLamports > 0) {
                const totalSol = totalLamports / LAMPORTS_PER_SOL;
                networkFeeDisplay = `~${formatNumber(totalSol, { maximumFractionDigits: 9 })} SOL`;
            }
        }

        return {
            priceImpactDisplay: quotePriceImpact !== undefined ? `${quotePriceImpact.toFixed(2)}%` : "-",
            routingFeeDisplay,
            networkFeeDisplay
        };
    }, [quoteForCurrentPair, quotePriceImpact]);

    const [feeFooterByPair, setFeeFooterByPair] = React.useState<Record<string, FeeFooterDisplay>>({});
    React.useEffect(() => {
        if (!exchangeRatePairKey || liveFeeFooterDisplay === null) return;
        setFeeFooterByPair((current) =>
            current[exchangeRatePairKey] === liveFeeFooterDisplay
                ? current
                : { ...current, [exchangeRatePairKey]: liveFeeFooterDisplay }
        );
    }, [exchangeRatePairKey, liveFeeFooterDisplay]);
    const cachedFeeFooterDisplay = exchangeRatePairKey ? feeFooterByPair[exchangeRatePairKey] : undefined;
    const feeFooterDisplay = liveFeeFooterDisplay ?? cachedFeeFooterDisplay;
    const priceImpactDisplay = feeFooterDisplay?.priceImpactDisplay ?? "-";
    const routingFeeDisplay = feeFooterDisplay?.routingFeeDisplay ?? "-";
    const networkFeeDisplay = feeFooterDisplay?.networkFeeDisplay ?? "-";
    const feeFooterLoading =
        isQuoteLoading && parseFloat(sendAmount) > 0 && !quoteError && feeFooterDisplay === undefined;

    const cardHeight = cardMinHeightValue;

    // ─── Swap feedback ───────────────────────────────────────────────────────

    const { ctaFeedback, isErrorFeedback, setInputError, rejectionCount } = useSwapFeedback({
        sendAmount,
        maxBalanceValue,
        sendTokenLabel: sendDisplayOption?.label,
        quoteError,
        quoteRejection: quoteRejection?.reason ?? null,
        sendTokenDecimals,
        priceImpact: quotePriceImpact,
        mode,
        receiveAmountDisplay,
        sendCurrencySymbol,
        receiveCurrencySymbol,
        solBalance: holdings?.native?.uiAmount ?? null
    });

    const swapValidationReason = !walletAddress
        ? "wallet_not_connected"
        : !sendAmount || !new BigDecimal(sendAmount).gt(0)
          ? "amount_required"
          : maxBalanceValue !== null && new BigDecimal(sendAmount).gt(maxBalanceValue)
            ? "insufficient_balance"
            : quoteError
              ? "quote_error"
              : isErrorFeedback
                ? "input_invalid"
                : !canExecuteQuote
                  ? "quote_not_ready"
                  : "ready";
    React.useEffect(() => {
        if (swapView !== "form") return;
        analytics.changed("validation_updated", {
            reason: swapValidationReason,
            can_submit: swapValidationReason === "ready",
            mode
        });
    }, [analytics, mode, swapValidationReason, swapView]);

    // ─── Handlers ────────────────────────────────────────────────────────────

    const maxDecimals = sendTokenDecimals;
    const MAX_U64 = React.useMemo(() => new BigDecimal("18446744073709551615"), []);

    const handleSendAmountChange = React.useCallback(
        (value: string): void => {
            if (value === "") {
                setSendAmount("");
                setReceiveAmount("");
                return;
            }

            // Replace comma with period for locales that use comma as decimal separator
            const sanitized = value.replace(",", ".");
            let normalizedValue = sanitized.startsWith(".") ? `0${sanitized}` : sanitized;
            normalizedValue =
                normalizedValue.length >= 2 && normalizedValue.startsWith("0") && normalizedValue[1] !== "."
                    ? normalizedValue.slice(1)
                    : normalizedValue;

            const regex = /^\d+(\.\d*)?$/;
            if (!regex.test(normalizedValue)) return;

            // Decimals stay unknown for a picked token until its Jupiter
            // metadata arrives; no quote is requested until then either.
            if (maxDecimals !== undefined) {
                const decimalPart = normalizedValue.split(".")[1];

                if (decimalPart?.length > maxDecimals) {
                    setInputError("decimals");
                    return;
                }

                const rawAmount = convertStringtoRaw(normalizedValue, maxDecimals) as BigDecimal;
                if (rawAmount.gt(MAX_U64)) {
                    setInputError("amountTooLarge");
                    return;
                }
            }

            const numValue = parseFloat(normalizedValue) || 0;

            setSendAmount(normalizedValue.replace(/^0+(?=\d)/, ""));

            if (numValue <= 0) {
                setReceiveAmount("");
            }

            // `swap_amount_first_entered` — once per session per (mode × send).
            if (numValue > 0) {
                const key = `${mode}:send`;
                if (!amountFirstEnteredKeysRef.current.has(key)) {
                    amountFirstEnteredKeysRef.current.add(key);
                    capture("swap_amount_first_entered", {
                        mode,
                        field: "send",
                        token_symbol: sendCurrency
                    });
                }
            }
        },
        [maxDecimals, MAX_U64, setInputError, mode, sendCurrency, capture]
    );

    const handleBlur = React.useCallback((): void => {
        if (sendAmount === "" || sendAmount === "0") {
            setSendAmount("");
            setReceiveAmount("");
            return;
        }

        const decimalPart = sendAmount.split(".")[1];
        if (decimalPart?.length < 2) {
            setSendAmount(Number(sendAmount).toFixed(2));
        }
    }, [sendAmount]);

    const handleMaxClick = React.useCallback((): void => {
        if (!maxBalanceAmount || !new BigDecimal(maxBalanceAmount).gt(0)) {
            setSendAmount("");
            return;
        }

        capture("swap_max_clicked", {
            mode,
            field: "send",
            token_symbol: sendCurrency
        });

        setSendAmount(maxBalanceAmount);
    }, [maxBalanceAmount, mode, sendCurrency, capture]);

    const swapSendReceive = React.useCallback((): void => {
        const tempAmount = sendAmount;
        const tempCurrency = sendCurrency;
        const tempSelectedOption = sendSelectedOption;

        setSendAmount(receiveAmount);
        setReceiveAmount(tempAmount);

        setSendCurrency(receiveCurrency);
        setReceiveCurrency(tempCurrency);

        setSendSelectedOption(receiveSelectedOption);
        setReceiveSelectedOption(tempSelectedOption);
    }, [sendAmount, sendCurrency, sendSelectedOption, receiveAmount, receiveCurrency, receiveSelectedOption]);

    const handleSwapClick = React.useCallback((): void => {
        swapSendReceive();
        const next = mode === "buy" ? "sell" : "buy";
        capture("swap_mode_switched", { from_mode: mode, to_mode: next });
        setMode(next);
    }, [swapSendReceive, capture, mode]);

    // Tab switches after a completed swap also dismiss the success view
    // (otherwise the receipt + "Bought/Sold X" CTA stays mounted with the
    // tab's labels recomputed against the wrong mode — e.g. switching to
    // Buy after a Sell would relabel the stale snapshot as "Bought").
    const resetIfNotForm = React.useCallback((): void => {
        if (swapView !== "form") {
            resetExecute();
            swapSnapshotRef.current = null;
            setSwapView("form");
        }
    }, [swapView, resetExecute]);

    const handleBuyTab = React.useCallback((): void => {
        resetIfNotForm();
        if (mode !== "buy") {
            capture("swap_mode_switched", {
                from_mode: mode,
                to_mode: "buy"
            });
            swapSendReceive();
            setMode("buy");
        }
    }, [mode, swapSendReceive, resetIfNotForm, capture]);

    const handleSellTab = React.useCallback((): void => {
        resetIfNotForm();
        if (mode !== "sell") {
            capture("swap_mode_switched", {
                from_mode: mode,
                to_mode: "sell"
            });
            swapSendReceive();
            setMode("sell");
        }
    }, [mode, swapSendReceive, resetIfNotForm, capture]);

    const handleCurrencyChange = React.useCallback(
        (field: "send" | "receive", value: string): void => {
            const fromSymbol = field === "send" ? sendCurrency : receiveCurrency;
            const toToken = tokens.find((t) => t.address === value);
            const toSymbol = toToken?.symbol ?? value;
            capture("swap_token_selected", {
                mode,
                field,
                from_symbol: fromSymbol,
                to_symbol: toSymbol,
                changed: fromSymbol !== value
            });
            if (field === "send") {
                setSendCurrency(value);
            } else {
                setReceiveCurrency(value);
            }
        },
        [mode, sendCurrency, receiveCurrency, tokens, capture]
    );

    const handleSuccessClose = React.useCallback((): void => {
        analytics.send("closed", { close_method: "return_to_form" });
        resetExecute();
        swapSnapshotRef.current = null;
        setSwapView("form");
    }, [resetExecute, analytics]);

    const handlePendingCancel = React.useCallback((): void => {
        analytics.send("canceled", { close_method: "return_to_form" });
        resetExecute();
        swapSnapshotRef.current = null;
        setSwapView("form");
    }, [resetExecute, analytics]);

    // Wallet-cancel → return to the form view directly (no "Buy/Sell Failed"
    // recovery UI). The user chose not to proceed; the input should be ready
    // for another try, not gated behind a 2s error transition.
    useAutoDismissUserCancel(executeState, handlePendingCancel);

    const handleSubmit = React.useCallback(
        async (cardHeight?: number): Promise<void> => {
            analytics.send("submit_requested", { mode });
            if (!executeParams) {
                analytics.send("blocked", { mode, reason: "quote_not_ready" });
                return;
            }

            const { acquired } = await withWalletBalanceMutationLock("swap", async () => {
                if (cardHeight !== undefined) {
                    setCardMinHeightValue(cardHeight);
                }

                // Compute full-precision receive amount from quote for reconciliation
                let receiveAmountRaw = receiveAmountDisplay;
                if (quoteForCurrentPair && receiveToken) {
                    receiveAmountRaw = convertRawToToken(
                        new BigDecimal(quoteForCurrentPair.outAmount),
                        receiveToken.decimals
                    ) as string;
                }

                swapSnapshotRef.current = {
                    // Store the raw normalized input string — `handleSendAmountChange`
                    // already sanitizes it. Avoid `parseFloat(...).toString()`, which
                    // re-encodes very small amounts in scientific notation (e.g.
                    // `0.000000001` → `"1e-9"`) and would feed `BigDecimal` an
                    // unparseable value when sell mode reconciles the optimistic delta.
                    sendAmount,
                    sendSymbol: sendDisplayOption?.label || "",
                    sendIcon: sendDisplayOption?.icon,
                    receiveAmount: receiveAmountDisplay,
                    receiveAmountRaw,
                    receiveSymbol: receiveDisplayOption?.label || "",
                    receiveIcon: receiveDisplayOption?.icon,
                    sendUsdValue: usdValue,
                    receiveUsdValue,
                    priceImpactDisplay,
                    routingFeeDisplay,
                    networkFeeDisplay
                };
                setSwapView("pending");

                const attempt = analytics.attempt({
                    mode,
                    source_token: swapToken(sendDisplayOption?.label),
                    target_token: swapToken(receiveDisplayOption?.label),
                    amount_bucket: swapAmountBucket(sendAmount)
                });
                let failed = false;
                const receipt = await executeSwap(executeParams, {
                    observer: {
                        stage: (stage) => {
                            attempt.stage(stage);
                            attempt.send("stage_changed", { stage });
                            if (stage === "submitting") attempt.send("submitted");
                        },
                        failed: (error, uncertain) => {
                            failed = true;
                            attempt.endFailure(error, uncertain);
                        }
                    }
                });
                const signature = receipt?.signature;
                if (!receipt || !signature) {
                    if (!failed) attempt.endFailure(new Error("Execution receipt unavailable"), !!receipt);
                    return null;
                }
                attempt.complete();

                const snapshot = swapSnapshotRef.current;
                if (snapshot) {
                    const delta: Record<string, BigDecimal> = {};
                    if (mode === "buy") {
                        delta.usdv = new BigDecimal(snapshot.receiveAmountRaw);
                    } else {
                        delta.usdv = new BigDecimal(snapshot.sendAmount).multiply(new BigDecimal(-1));
                    }

                    setSendAmount("");
                    setReceiveAmount("");
                    setSwapView("success");

                    await reconcileWithOptimisticDelta(delta);
                } else {
                    setSwapView("success");
                }

                await queryClient.invalidateQueries({
                    queryKey: ["jupiter-holdings", walletAddress]
                });
                await queryClient.invalidateQueries({
                    queryKey: ["transactionHistory"]
                });

                return signature;
            });

            if (!acquired) {
                analytics.send("blocked", { reason: "transaction_in_progress" });
                capture("user_facing_error_shown", {
                    surface: "toast",
                    error_category: "validation",
                    error_message: "Transaction already processing",
                    route: typeof window !== "undefined" ? window.location.pathname : ""
                });
                toast.error("Transaction already processing");
            }
        },
        [
            analytics,
            capture,
            executeParams,
            receiveAmountDisplay,
            quoteForCurrentPair,
            receiveToken,
            sendAmount,
            sendDisplayOption,
            receiveDisplayOption,
            usdValue,
            receiveUsdValue,
            priceImpactDisplay,
            routingFeeDisplay,
            networkFeeDisplay,
            executeSwap,
            mode,
            reconcileWithOptimisticDelta,
            queryClient,
            walletAddress
        ]
    );

    // ─── Analytics: quote lifecycle ──────────────────────────────────────────
    // Capture the moment quote params become truthy so `quote_latency_ms` can
    // be measured against the quote arrival.
    React.useEffect(() => {
        if (quoteParams && quoteRequestedAtRef.current === null) {
            quoteRequestedAtRef.current = Date.now();
        }
        if (!quoteParams) {
            quoteRequestedAtRef.current = null;
            lastQuoteSignatureRef.current = null;
            lastQuoteErrorKeyRef.current = null;
        }
    }, [quoteParams]);

    React.useEffect(() => {
        if (!quoteForCurrentPair) return;
        // De-dup on requestId+amounts so we don't fire on every memo re-eval.
        const sig = `${quoteForCurrentPair.requestId ?? ""}|${quoteForCurrentPair.inAmount ?? ""}|${quoteForCurrentPair.outAmount ?? ""}`;
        if (lastQuoteSignatureRef.current === sig) return;
        lastQuoteSignatureRef.current = sig;

        const startedAt = quoteRequestedAtRef.current;
        const sendUsdNum = parseFloat(usdValue);
        capture("swap_quote_received", {
            mode,
            send_symbol: sendDisplayOption?.label ?? "",
            receive_symbol: receiveDisplayOption?.label ?? "",
            send_amount: sendAmount,
            receive_amount: receiveAmountDisplay,
            send_amount_usd: Number.isFinite(sendUsdNum) ? sendUsdNum : undefined,
            routing_fee_bps: quoteForCurrentPair.platformFee?.feeBps ?? 0,
            quote_latency_ms: startedAt !== null ? Date.now() - startedAt : undefined
        });
        quoteRequestedAtRef.current = null;
    }, [
        capture,
        quoteForCurrentPair,
        mode,
        sendDisplayOption,
        receiveDisplayOption,
        sendAmount,
        receiveAmountDisplay,
        usdValue
    ]);

    React.useEffect(() => {
        if (!quoteError) return;
        const key = quoteError.message || String(quoteError);
        if (lastQuoteErrorKeyRef.current === key) return;
        lastQuoteErrorKeyRef.current = key;

        const lower = key.toLowerCase();
        const errorCode: "no_route" | "rate_limit" | "network" | "unknown" =
            lower.includes("no route") || lower.includes("not found")
                ? "no_route"
                : lower.includes("rate") || lower.includes("429")
                  ? "rate_limit"
                  : lower.includes("network") || lower.includes("fetch")
                    ? "network"
                    : "unknown";
        capture("swap_quote_failed", {
            mode,
            send_symbol: sendDisplayOption?.label ?? "",
            receive_symbol: receiveDisplayOption?.label ?? "",
            send_amount: sendAmount,
            error_code: errorCode,
            error_message: key
        });
    }, [quoteError, mode, sendDisplayOption, receiveDisplayOption, sendAmount, capture]);

    // A priced quote Jupiter would not build is a quote failure too; report
    // its documented reason rather than lumping it in with "unknown".
    React.useEffect(() => {
        if (!quoteRejection) return;
        const key = `rejection:${quoteRejection.router ?? ""}:${quoteRejection.errorCode ?? ""}`;
        if (lastQuoteErrorKeyRef.current === key) return;
        lastQuoteErrorKeyRef.current = key;

        capture("swap_quote_failed", {
            mode,
            send_symbol: sendDisplayOption?.label ?? "",
            receive_symbol: receiveDisplayOption?.label ?? "",
            send_amount: sendAmount,
            error_code: quoteRejection.reason,
            error_message: quoteRejection.errorMessage ?? ""
        });
    }, [quoteRejection, mode, sendDisplayOption, receiveDisplayOption, sendAmount, capture]);

    // ─── Return ──────────────────────────────────────────────────────────────

    return React.useMemo<UseSwapReturn>(
        () => ({
            analyticsCapture: capture,
            mode,
            swapView,
            swapSnapshot: swapSnapshotRef.current,
            cardHeight,
            sendAmount,
            sendCurrency,
            receiveCurrency,
            sendAssetOptions,
            receiveAssetOptions,
            sendDisplayOption,
            receiveDisplayOption,
            receiveAmountDisplay,
            sendDisplayBalance,
            receiveDisplayBalance,
            usdValue,
            receiveUsdValue,
            exchangeRate,
            priceImpactDisplay,
            routingFeeDisplay,
            networkFeeDisplay,
            feeFooterLoading,
            isQuoteLoading,
            isQuoteFetching,
            quoteError,
            refetchQuote,
            executeState,
            ctaFeedback,
            isErrorFeedback,
            rejectionCount,
            connected,
            maxBalanceValue,
            quote: quoteForCurrentPair,
            canExecuteQuote,
            sendCurrencySymbol,
            receiveCurrencySymbol,
            sendTokenDecimals,
            receiveTokenDecimals,
            handleSendAmountChange,
            handleBlur,
            handleMaxClick,
            handleSwapClick,
            handleBuyTab,
            handleSellTab,
            handleCurrencyChange,
            handleSuccessClose,
            handleSubmit,
            handlePendingCancel,
            setSendSelectedOption,
            setReceiveSelectedOption
        }),
        [
            capture,
            mode,
            swapView,
            cardHeight,
            sendAmount,
            sendCurrency,
            receiveCurrency,
            sendAssetOptions,
            receiveAssetOptions,
            sendDisplayOption,
            receiveDisplayOption,
            receiveAmountDisplay,
            sendDisplayBalance,
            receiveDisplayBalance,
            usdValue,
            receiveUsdValue,
            exchangeRate,
            priceImpactDisplay,
            routingFeeDisplay,
            networkFeeDisplay,
            feeFooterLoading,
            isQuoteLoading,
            isQuoteFetching,
            quoteError,
            refetchQuote,
            executeState,
            ctaFeedback,
            isErrorFeedback,
            rejectionCount,
            connected,
            maxBalanceValue,
            quoteForCurrentPair,
            canExecuteQuote,
            sendCurrencySymbol,
            receiveCurrencySymbol,
            sendTokenDecimals,
            receiveTokenDecimals,
            handleSendAmountChange,
            handleBlur,
            handleMaxClick,
            handleSwapClick,
            handleBuyTab,
            handleSellTab,
            handleCurrencyChange,
            handleSuccessClose,
            handleSubmit,
            handlePendingCancel
        ]
    );
}
